import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Pencil,
  Trash2,
  Search,
  X,
  UserPlus,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { db } from '../database/supabase';
import { authApi } from '../database/authApi';
import { useAuth } from '../contexts/AuthContext';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'staff', label: 'Staff' },
  { value: 'student', label: 'Student' },
];

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  role: 'staff',
  password: '',
  status: 'approved',
  is_active: true,
};

export default function UsersManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await db.getUsers();
      if (err) throw err;
      setUsers(data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'staff',
      password: '',
      status: user.status || (user.is_active ? 'approved' : 'pending'),
      is_active: user.is_active !== false && user.status !== 'pending',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const status = form.status;
      const isActive = status === 'approved' ? !!form.is_active : false;
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        role: form.role,
        status,
        is_active: isActive,
        updated_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      };
      if (status === 'approved') {
        payload.approved_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
      }
      if (form.password.trim()) {
        payload.password = form.password.trim();
      }

      if (editing) {
        if (!form.password.trim()) {
          delete payload.password;
        }
        const { data, error: err } = await db.updateUser(editing.id, payload);
        if (err) throw err;
        if (form.password.trim()) {
          await authApi.setPassword(editing.id, form.password.trim());
        }
        setUsers((prev) => prev.map((u) => (u.id === editing.id ? { ...u, ...data } : u)));
        toast.success('User updated');
      } else {
        if (!form.password.trim() || form.password.trim().length < 6) {
          throw new Error('Password (min 6 characters) is required for new users');
        }
        const { data, error: err } = await db.createUser({
          ...payload,
          password: form.password.trim(),
          created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        });
        if (err) throw err;
        setUsers((prev) => [data, ...prev]);
        toast.success('User created');
      }
      setModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name || user.email}"? This cannot be undone.`)) {
      return;
    }
    try {
      const { error: err } = await db.deleteUser(user.id);
      if (err) throw err;
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('User deleted');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleApprove = async (user) => {
    try {
      const { data, error } = await authApi.approve(user.id, currentUser?.id || null);
      if (error) throw new Error(error.message);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...data } : u)));
      toast.success(`${user.name || user.email} approved`);
    } catch (err) {
      toast.error(err.message || 'Failed to approve user');
    }
  };

  const handleReject = async (user) => {
    if (!window.confirm(`Reject registration for "${user.name || user.email}"?`)) return;
    try {
      const { data, error } = await authApi.reject(user.id);
      if (error) throw new Error(error.message);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...data } : u)));
      toast.success('Registration rejected');
    } catch (err) {
      toast.error(err.message || 'Failed to reject user');
    }
  };

  const toggleActive = async (user) => {
    if ((user.status || 'pending') !== 'approved') {
      toast.error('Only approved users can be activated');
      return;
    }
    try {
      const { data, error: err } = await db.updateUser(user.id, {
        is_active: !user.is_active,
        updated_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      });
      if (err) throw err;
      setUsers((prev) => prev.map((u) => (u.id === user.id ? data : u)));
      toast.success(`User ${data.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  const pendingCount = users.filter((u) => (u.status || '') === 'pending').length;

  const filtered = users.filter((u) => {
    const status = u.status || (u.is_active ? 'approved' : 'pending');
    if (statusFilter && status !== statusFilter) return false;
    if (roleFilter && u.role !== roleFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q)
    );
  });

  const roleBadge = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'staff':
        return 'bg-blue-100 text-blue-800';
      case 'student':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Users</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button type="button" onClick={loadUsers} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600">
            Create, update, and approve user accounts
            {pendingCount > 0 && (
              <span className="ml-2 status-badge bg-yellow-100 text-yellow-800">
                {pendingCount} pending
              </span>
            )}
          </p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </button>
      </div>

      <div className="bg-white rounded-lg shadow mb-6 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="form-input pl-10"
            placeholder="Search name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="form-input max-w-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          className="form-input max-w-xs"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="admin-table-header">Name</th>
                <th className="admin-table-header">Email</th>
                <th className="admin-table-header">Phone</th>
                <th className="admin-table-header">Role</th>
                <th className="admin-table-header">Approval</th>
                <th className="admin-table-header">Active</th>
                <th className="admin-table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filtered.map((user) => {
                  const status = user.status || (user.is_active ? 'approved' : 'pending');
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="admin-table-cell font-medium">{user.name || '—'}</td>
                      <td className="admin-table-cell">{user.email}</td>
                      <td className="admin-table-cell">{user.phone || '—'}</td>
                      <td className="admin-table-cell">
                        <span className={`status-badge ${roleBadge(user.role)}`}>
                          {user.role || 'unknown'}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <span className={`status-badge ${statusBadge(status)}`}>{status}</span>
                      </td>
                      <td className="admin-table-cell">
                        <button
                          type="button"
                          onClick={() => toggleActive(user)}
                          className={`status-badge ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="admin-table-cell">
                        <div className="flex items-center gap-1">
                          {status === 'pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(user)}
                                className="p-2 text-green-700 hover:bg-green-50 rounded"
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleReject(user)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(user)}
                            className="p-2 text-blue-800 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(user)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 text-sm text-gray-500">
          Showing {filtered.length} of {users.length} users
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {editing ? 'Edit User' : 'Add User'}
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="form-label">Full Name *</label>
                <input
                  className="form-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input
                  className="form-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Role *</label>
                <select
                  className="form-input"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Approval Status *</label>
                <select
                  className="form-input"
                  value={form.status}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value,
                      is_active: e.target.value === 'approved' ? form.is_active : false,
                    })
                  }
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="form-label">
                  {editing ? 'New Password (optional)' : 'Password *'}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? 'Leave blank to keep current' : 'Min 6 characters'}
                  required={!editing}
                  minLength={editing && !form.password ? undefined : 6}
                />
              </div>
              {form.status === 'approved' && (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Active account (can sign in)
                </label>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
